import {useCallback, useEffect, useRef} from "react";
import {ActiveSelection, FabricObject} from "fabric";
import {useTranslation} from "react-i18next";
import {FloorPlanItem} from "../types";
import {generateId} from "../../../../../utils/generate-id";
import {DUPLICATE_OFFSET} from "../utils/constants";
import {
    createShapeElement,
    createTableGroup,
    createTextElement,
    createWallElement
} from "../utils/fabric-factory";
import {useFloorplanContext} from "./useFloorplanContext";
import {
    getNormalizedShapeCanvas,
    getNormalizedTableCanvas,
    getNormalizedTextCanvas,
    getNormalizedWallPoints,
} from "../utils/resize-utils";
import {
    getObjectAnchorPoint,
    offsetPoint,
    snapPoint,
    syncFloorPlanItemTransform,
} from "../utils/snap-geometry";

// Alt/Option-drag should behave like Figma: keep the original in place and turn the dragged
// object into the duplicate without replacing the existing Cmd/Ctrl+D duplication flow.
interface Props {
    onDuplicated: (elements: FabricObject[], isSingle: boolean) => void;
    onDeleted: () => void;
}

const isTypingTarget = (t: EventTarget | null) => {
    if (!t || !(t instanceof HTMLElement)) return false;
    const tag = t.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable;
};

const appendDuplicateSuffix = (name: string, suffix: string) => `${name} (${suffix})`;

/** Deep-copy arrays/objects inside canvas data so duplicates don't share references (e.g. points, strokeDashArray). */
const deepCopyCanvas = <T extends Record<string, any>>(canvas: T): T => {
    const copy = {...canvas};
    for (const key of Object.keys(copy)) {
        const val = copy[key];
        if (Array.isArray(val)) {
            (copy as any)[key] = val.map((v: any) =>
                v && typeof v === 'object' ? {...v} : v
            );
        }
    }
    return copy;
};

export function useElementDuplication({
                                          onDuplicated,
                                          onDeleted
                                      }: Props) {
    const {canvas, showSeatsAroundTable} = useFloorplanContext();
    const {i18n, t} = useTranslation();
    const altDragState = useRef<{
        source: FabricObject | null;
        startAnchor: { x: number; y: number };
        startAngle: number;
        duplicated: boolean;
    }>({
        source: null,
        startAnchor: {x: 0, y: 0},
        startAngle: 0,
        duplicated: false,
    });


    const createDuplicateElement = useCallback(
        (
            element: FabricObject,
            offset: number,
            shouldSnap = true
        ): FabricObject | null => {
            const item = element.floorPlanItem as FloorPlanItem | undefined;
            if (!item) return null;

            const angle = element.angle ?? 0;
            const anchor = getObjectAnchorPoint(element);
            const nextAnchor = shouldSnap
                ? snapPoint(offsetPoint(anchor, offset))
                : offsetPoint(anchor, offset);
            const duplicateSuffix = i18n.resolvedLanguage?.startsWith('de') ? 'Kopie' : 'duplicate';

            if (item.table) {
                const id = generateId();
                const fallbackName = item.table.shape === "circle" ? t('Round Table') : t('Table');
                const {
                    id: _existingResourceId,
                    ...resourceWithoutId
                } = (item.table.resource ?? {}) as any;

                const seats = item.table.seats;
                return createTableGroup(
                    {
                        id,
                        table: {
                            ...item.table,
                            // Keep duplicated tables as "new" resources so save uses create, not update.
                            resource: {
                                ...resourceWithoutId,
                                name: appendDuplicateSuffix(item.table.resource?.name ?? fallbackName, duplicateSuffix),
                                type: "table"
                            } as any,
                            canvas: {
                                ...getNormalizedTableCanvas(item.table.canvas, item.table.shape, element.scaleX, element.scaleY),
                                id,
                                left: nextAnchor.x,
                                top: nextAnchor.y,
                                angle,
                            },
                            seats: seats
                                ? {...seats, edges: {...seats.edges}, slots: seats.slots.map(s => ({...s}))}
                                : undefined,
                        },
                    },
                    showSeatsAroundTable
                );
            }

            if (item.element) {
                const id = generateId();

                // We duplicate the *same kind* of element, but with a new id + snapped position.
                if (item.element.kind === "text") {
                    return createTextElement({
                        id,
                        element: {
                            ...item.element,
                            canvas: {
                                ...getNormalizedTextCanvas(item.element.canvas, element.scaleX, element.scaleY),
                                id,
                                left: nextAnchor.x,
                                top: nextAnchor.y,
                                angle
                            },
                        },
                    });
                }

                if (item.element.kind === "shape" && item.element.shape) {
                    return createShapeElement({
                        id,
                        element: {
                            ...item.element,
                            canvas: {
                                ...getNormalizedShapeCanvas(item.element.canvas, item.element.shape, element.scaleX, element.scaleY),
                                ...(item.element.canvas.label ? {label: appendDuplicateSuffix(item.element.canvas.label, duplicateSuffix)} : {}),
                                id,
                                left: nextAnchor.x,
                                top: nextAnchor.y,
                                angle
                            },
                        },
                    });
                }

                if (item.element.kind === "wall") {
                    return createWallElement({
                        id,
                        element: {
                            ...item.element,
                            canvas: {
                                ...deepCopyCanvas(item.element.canvas),
                                points: getNormalizedWallPoints(item.element.canvas, element.scaleX, element.scaleY),
                                scaleX: 1,
                                scaleY: 1,
                                ...(item.element.canvas.label ? {label: appendDuplicateSuffix(item.element.canvas.label, duplicateSuffix)} : {}),
                                id,
                                left: nextAnchor.x,
                                top: nextAnchor.y,
                                angle
                            },
                        },
                    });
                }
            }

            return null;
        },
        [i18n.resolvedLanguage, showSeatsAroundTable, t]
    );

    const duplicateElement = useCallback(
        (element: FabricObject) => {
            if (!canvas) return;

            const dup = createDuplicateElement(element, DUPLICATE_OFFSET);
            if (!dup) return;

            canvas.add(dup);
            dup.setCoords();
            canvas.discardActiveObject();
            canvas.setActiveObject(dup);
            canvas.requestRenderAll();

            onDuplicated([dup], true);
        },
        [canvas, createDuplicateElement, onDuplicated]
    );

    useEffect(() => {
        if (!canvas) return;

        const resetAltDragState = () => {
            altDragState.current = {
                source: null,
                startAnchor: {x: 0, y: 0},
                startAngle: 0,
                duplicated: false,
            };
        };

        const handleMouseDown = (e: any) => {
            const target = e.target as FabricObject | undefined;
            const nativeEvent = e.e as MouseEvent | undefined;

            if (!nativeEvent?.altKey || !target?.floorPlanItem || target instanceof ActiveSelection) {
                resetAltDragState();
                return;
            }

            const activeObjects = canvas
                .getActiveObjects()
                .filter((obj) => !!(obj as any).floorPlanItem);
            if (activeObjects.length > 1) {
                resetAltDragState();
                return;
            }

            altDragState.current = {
                source: target,
                startAnchor: getObjectAnchorPoint(target),
                startAngle: target.angle ?? 0,
                duplicated: false,
            };
        };

        const handleObjectMoving = (e: any) => {
            const state = altDragState.current;
            const target = e.target as FabricObject | undefined;
            const nativeEvent = e.e as MouseEvent | undefined;

            if (!nativeEvent?.altKey || !target || !state.source || state.duplicated || target !== state.source) {
                return;
            }

            const activeObjects = canvas
                .getActiveObjects()
                .filter((obj) => !!(obj as any).floorPlanItem);
            if (activeObjects.length > 1) {
                return;
            }

            const duplicate = createDuplicateElement(target, 0, false);
            const sourceItem = target.floorPlanItem as FloorPlanItem | undefined;
            const duplicateItem = duplicate?.floorPlanItem as FloorPlanItem | undefined;
            if (!duplicate || !sourceItem || !duplicateItem) return;

            // The newly created Fabric object stays behind as the original at drag start.
            duplicate.floorPlanItem = syncFloorPlanItemTransform(
                sourceItem,
                state.startAnchor.x,
                state.startAnchor.y,
                state.startAngle
            );
            duplicate.set({
                left: state.startAnchor.x,
                top: state.startAnchor.y,
                angle: state.startAngle,
            });
            duplicate.setCoords();

            // The object under the pointer keeps moving, but now carries the duplicated item data.
            const currentAnchor = getObjectAnchorPoint(target);
            target.floorPlanItem = syncFloorPlanItemTransform(
                duplicateItem,
                currentAnchor.x,
                currentAnchor.y,
                target.angle ?? state.startAngle
            );

            canvas.add(duplicate);
            canvas.requestRenderAll();
            state.duplicated = true;
        };

        canvas.on('mouse:down', handleMouseDown);
        canvas.on('object:moving', handleObjectMoving);
        canvas.on('mouse:up', resetAltDragState);

        return () => {
            canvas.off('mouse:down', handleMouseDown);
            canvas.off('object:moving', handleObjectMoving);
            canvas.off('mouse:up', resetAltDragState);
        };
    }, [canvas, createDuplicateElement]);

    useEffect(() => {
        if (!canvas) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't hijack shortcuts while user is typing/editing text fields.
            if (isTypingTarget(e.target)) return;

            // Delete selection
            if (e.key === "Delete" || e.key === "Backspace") {
                const selected = canvas.getActiveObjects().filter((o) => !!(o as any).floorPlanItem);
                if (selected.length === 0) return;

                selected.forEach((o) => canvas.remove(o));
                canvas.discardActiveObject();
                canvas.requestRenderAll();
                onDeleted();
                return;
            }

            // Duplicate selection (Cmd/Ctrl + D)
            if ((e.metaKey || e.ctrlKey) && (e.key === "d" || e.key === "D")) {
                e.preventDefault();
                if (e.repeat) return;

                const selected = canvas.getActiveObjects().filter((o) => !!(o as any).floorPlanItem);
                if (selected.length === 0) return;

                // When duplicating a multi-select, objects may be in an ActiveSelection coordinate context;
                // using absolute positions keeps the copied layout aligned.
                const newElements: FabricObject[] = [];
                for (const obj of selected) {
                    const dup = createDuplicateElement(obj, DUPLICATE_OFFSET);
                    if (!dup) continue;
                    canvas.add(dup);
                    dup.setCoords();
                    newElements.push(dup);
                }

                if (newElements.length === 0) return;

                canvas.discardActiveObject();
                if (newElements.length === 1) {
                    canvas.setActiveObject(newElements[0]);
                } else {
                    canvas.setActiveObject(new ActiveSelection(newElements, {canvas}));
                }
                canvas.requestRenderAll();

                onDuplicated(newElements, newElements.length === 1);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        }
    }, [canvas, createDuplicateElement, onDuplicated, onDeleted]);

    return {
        duplicateElement
    };
}
